/**
 * Per-user "pinned projects" for the projects grid.
 *
 * WHY THIS EXISTS
 * [[projectActivity]] already lifts recently-opened projects, but that signal is
 * automatic and time-decaying: a project you check weekly sinks below one you
 * opened once by accident. A pin is the deliberate, sticky counterpart — it
 * says "keep this at the top" and never decays.
 *
 * WHY A SEPARATE KEY FROM rsaProjectActivity
 * That blob is a flat `id -> ISO string` map committed to coexisting with a
 * future server-side projectusers.last_activity column. Folding pins into it
 * would force a migration of every user's recorded opens and conflate two
 * different lifetimes: pins are permanent until revoked, activity is a rolling
 * 500-entry LRU.
 *
 * WHY localStorage AND NOT IndexedDB
 * Same reason as projectActivity: the Set is consumed inside a synchronous sort,
 * so it must be present on first paint. An async store would render the
 * unpinned order and visibly reshuffle a tick later.
 *
 * KNOWN LIMIT
 * Per-browser, like projectActivity. Pinning on your laptop does not reorder
 * your desktop's grid. Cross-device sync needs the same server-side work
 * described in docs/superpowers/specs/2026-08-04-project-last-activity-sort-design.md.
 */

const STORAGE_KEY = 'rsaProjectPins';

const readAll = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch (error) {
    // Corrupt JSON, or storage blocked (Safari private mode). Sorting must still
    // work, so degrade to "nothing pinned" rather than throwing into render.
    console.warn('projectPins: could not read store, ignoring', error);
    return {};
  }
};

/**
 * @returns {Set<string>} pinned project ids for this user. Always a Set; never throws.
 */
export const getPinnedProjects = (email) => {
  const forUser = readAll()[email];
  return new Set(Array.isArray(forUser) ? forUser.map(String) : []);
};

/**
 * @returns {boolean} whether this project is pinned by this user.
 */
export const isProjectPinned = (projectId, email) =>
  getPinnedProjects(email).has(String(projectId));

/**
 * Pin or unpin a project.
 *
 * @returns {Set<string>} the resulting pinned set. On a write failure this is the
 *   UNCHANGED set, so the UI never renders a pin that was not persisted.
 */
export const setProjectPinned = (projectId, email, pinned) => {
  const current = getPinnedProjects(email);
  if (projectId === undefined || projectId === null || projectId === '') return current;

  const next = new Set(current);
  if (pinned) {
    next.add(String(projectId));
  } else {
    next.delete(String(projectId));
  }

  try {
    const all = readAll();
    all[email] = [...next];
    localStorage.setItem(STORAGE_KEY, JSON.stringify(all));
    return next;
  } catch (error) {
    console.warn('projectPins: could not persist pin', error);
    return current;
  }
};

/**
 * Comparator fragment: pinned projects sort ahead of unpinned ones.
 *
 * Returns 0 when both sides share a pinned state, so the caller's own ordering
 * decides. Callers MUST apply this before any ASC/DESC inversion — otherwise
 * an ascending sort flips the pinned block to the bottom.
 *
 * @returns {-1|0|1}
 */
export const comparePinnedFirst = (pinnedIds, a, b) => {
  const aPin = pinnedIds.has(String(a?.Id));
  const bPin = pinnedIds.has(String(b?.Id));
  if (aPin === bPin) return 0;
  return aPin ? -1 : 1;
};
